// Driver's license issuing states/provinces (US, Canada, Mexico).
// The key (full name) is the value saved to `license_state`; short codes are not unique
// across countries (e.g. BC = British Columbia and Baja California), so they're display only.

export type LicenseCountry = 'US' | 'CA' | 'MX';

export interface LicenseState {
  short: string;
  alt?: string[];
  country: LicenseCountry;
}

export const LICENSE_STATES: Record<string, LicenseState> = {
  Alabama: { short: 'AL', country: 'US' },
  Alaska: { short: 'AK', country: 'US' },
  Arizona: { short: 'AZ', country: 'US' },
  Arkansas: { short: 'AR', country: 'US' },
  California: { short: 'CA', country: 'US' },
  Colorado: { short: 'CO', country: 'US' },
  Connecticut: { short: 'CT', country: 'US' },
  'District of Columbia': { short: 'DC', country: 'US' },
  'Washington, DC': { short: 'DC', country: 'US' },
  Delaware: { short: 'DE', country: 'US' },
  Florida: { short: 'FL', country: 'US' },
  Georgia: { short: 'GA', country: 'US' },
  Hawaii: { short: 'HI', country: 'US' },
  Idaho: { short: 'ID', country: 'US' },
  Illinois: { short: 'IL', country: 'US' },
  Indiana: { short: 'IN', country: 'US' },
  Iowa: { short: 'IA', country: 'US' },
  Kansas: { short: 'KS', country: 'US' },
  Kentucky: { short: 'KY', country: 'US' },
  Louisiana: { short: 'LA', country: 'US' },
  Maine: { short: 'ME', country: 'US' },
  Maryland: { short: 'MD', country: 'US' },
  Massachusetts: { short: 'MA', country: 'US' },
  Michigan: { short: 'MI', country: 'US' },
  Minnesota: { short: 'MN', country: 'US' },
  Mississippi: { short: 'MS', country: 'US' },
  Missouri: { short: 'MO', country: 'US' },
  Montana: { short: 'MT', country: 'US' },
  Nebraska: { short: 'NE', country: 'US' },
  Nevada: { short: 'NV', country: 'US' },
  'New Hampshire': { short: 'NH', country: 'US' },
  'New Jersey': { short: 'NJ', country: 'US' },
  'New Mexico': { short: 'NM', country: 'US' },
  'New York': { short: 'NY', country: 'US' },
  'North Carolina': { short: 'NC', country: 'US' },
  'North Dakota': { short: 'ND', country: 'US' },
  Ohio: { short: 'OH', country: 'US' },
  Oklahoma: { short: 'OK', country: 'US' },
  Oregon: { short: 'OR', country: 'US' },
  Pennsylvania: { short: 'PA', country: 'US' },
  'Rhode Island': { short: 'RI', country: 'US' },
  'South Carolina': { short: 'SC', country: 'US' },
  'South Dakota': { short: 'SD', country: 'US' },
  Tennessee: { short: 'TN', country: 'US' },
  Texas: { short: 'TX', country: 'US' },
  Utah: { short: 'UT', country: 'US' },
  Vermont: { short: 'VT', country: 'US' },
  Virginia: { short: 'VA', country: 'US' },
  Washington: { short: 'WA', country: 'US' },
  'West Virginia': { short: 'WV', country: 'US' },
  Wisconsin: { short: 'WI', country: 'US' },
  Wyoming: { short: 'WY', country: 'US' },
  'American Samoa': { short: 'AS', country: 'US' },
  Guam: { short: 'GU', country: 'US' },
  'Northern Mariana Islands': { short: 'MP', country: 'US' },
  'Puerto Rico': { short: 'PR', country: 'US' },
  'United States Minor Outlying Islands': { short: 'UM', country: 'US' },
  'Virgin Islands': { short: 'VI', country: 'US' },
  Alberta: { short: 'AB', country: 'CA' },
  'British Columbia': { short: 'BC', country: 'CA' },
  Manitoba: { short: 'MB', country: 'CA' },
  'New Brunswick': { short: 'NB', country: 'CA' },
  'Newfoundland and Labrador': { short: 'NL', country: 'CA' },
  'Nova Scotia': { short: 'NS', country: 'CA' },
  Nunavut: { short: 'NU', country: 'CA' },
  'Northwest Territories': { short: 'NT', country: 'CA' },
  Ontario: { short: 'ON', country: 'CA' },
  'Prince Edward Island': { short: 'PE', country: 'CA' },
  Quebec: { short: 'QC', country: 'CA' },
  Saskatchewan: { short: 'SK', country: 'CA' },
  Yukon: { short: 'YT', country: 'CA' },
  'Baja California': { short: 'BC', alt: ['BCN'], country: 'MX' },
  'Baja California Sur': { short: 'BS', alt: ['BCS'], country: 'MX' },
  'Federal District': { short: 'DF', alt: ['DIF'], country: 'MX' },
  'Nuevo León': { short: 'NL', alt: ['NLE'], country: 'MX' },
  'Quintana Roo': { short: 'QR', alt: ['Q Roo', 'ROO'], country: 'MX' },
  'San Luis Potosí': { short: 'SL', alt: ['SLP'], country: 'MX' },
  Aguascalientes: { short: 'AG', alt: ['AGS'], country: 'MX' },
  Campeche: { short: 'CM', alt: ['Camp', 'CAM'], country: 'MX' },
  Chiapas: { short: 'CS', alt: ['Chis', 'CHP'], country: 'MX' },
  Chihuahua: { short: 'CH', alt: ['Chih', 'CHH'], country: 'MX' },
  Coahuila: { short: 'MX', alt: ['Coah', 'COA'], country: 'MX' },
  Colima: { short: 'CL', alt: ['COL'], country: 'MX' },
  Durango: { short: 'DG', alt: ['Dgo', 'DUR'], country: 'MX' },
  Guanajuato: { short: 'GT', alt: ['Gto', 'GUA'], country: 'MX' },
  Guerrero: { short: 'GR', alt: ['Gro', 'GRO'], country: 'MX' },
  Hidalgo: { short: 'HG', alt: ['Hgo', 'HID'], country: 'MX' },
  Jalisco: { short: 'JA', alt: ['Jal', 'JAL'], country: 'MX' },
  Mexico: { short: 'ME', alt: ['Edomex', 'MEX'], country: 'MX' },
  México: { short: 'ME', alt: ['Edomex', 'MEX'], country: 'MX' },
  Michoacán: { short: 'MI', alt: ['Mich', 'MIC'], country: 'MX' },
  Morelos: { short: 'MO', alt: ['Mor', 'MOR'], country: 'MX' },
  Nayarit: { short: 'NA', alt: ['Nay', 'NAY'], country: 'MX' },
  Oaxaca: { short: 'OA', alt: ['Oax', 'OAX'], country: 'MX' },
  Puebla: { short: 'PU', alt: ['Pue', 'PUE'], country: 'MX' },
  Querétaro: { short: 'QE', alt: ['Qro', 'QUE'], country: 'MX' },
  Sinaloa: { short: 'SI', alt: ['SIN'], country: 'MX' },
  Sonora: { short: 'SO', alt: ['SON'], country: 'MX' },
  Tabasco: { short: 'TB', alt: ['TAB'], country: 'MX' },
  Tamaulipas: { short: 'TM', alt: ['Tamps', 'TAM'], country: 'MX' },
  Tlaxcala: { short: 'TL', alt: ['Tlax', 'TLA'], country: 'MX' },
  Veracruz: { short: 'VE', alt: ['VER'], country: 'MX' },
  Yucatán: { short: 'YU', alt: ['YUC'], country: 'MX' },
  Zacatecas: { short: 'ZA', alt: ['ZAC'], country: 'MX' },
};

const COUNTRY_LABELS: Record<LicenseCountry, string> = {
  US: 'United States',
  CA: 'Canada',
  MX: 'Mexico',
};

// Grouped options for an antd Select; `search` holds every name/code an admin might type
export const licenseStateOptions = (Object.keys(COUNTRY_LABELS) as LicenseCountry[]).map(
  (country) => ({
    label: COUNTRY_LABELS[country],
    options: Object.entries(LICENSE_STATES)
      .filter(([, state]) => state.country === country)
      .map(([name, state]) => ({
        value: name,
        label: `${name} (${state.short})`,
        search: [name, state.short, ...(state.alt ?? [])].join(' ').toLowerCase(),
      })),
  }),
);
